import React, { useMemo, useRef, useState } from "react";
import DayPopover from "./DayPopover";
import PlannerContextMenu from "./PlannerContextMenu";
import RecurringScheduleConfirmModal from "./RecurringScheduleConfirmModal";
import { plannerAPI } from "../../api/client";
import { formatLocalDate } from "../../utils/dateUtils";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const getCellStyle = (dayInfo, isSelected) => {
  switch (dayInfo?.type) {
    case "WEEKLY_OFF":
      return { backgroundColor: "#cbdef1", borderColor: "#E5E7EB" };
    case "HOLIDAY":
      return { backgroundColor: "#ecc494", borderColor: "#FED7AA" };
    case "OFF":
      // CHANGE THIS HEX to customize leave-tile color in Month view.
      return { backgroundColor: "#d8a9c7", borderColor: "#fecaca" };

    case "HALF_DAY":
      return {
        background: "linear-gradient(135deg, #FFFFFF 50%, #F3F4F6 50%)",
        borderColor: "#E5E7EB",
      };
    default:
      return {
        backgroundColor: "#FFFFFF",
        borderColor: isSelected ? "#4F46E5" : "#E5E7EB",
      };
  }
};

export default function MonthCalendar({
  year,
  month,
  data,
  isTeamView,
  isAdmin,
  onDayDoubleClick,
  onRefresh,
}) {
  const [selectedDay, setSelectedDay] = useState(null);
  const [popoverDay, setPopoverDay] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const clickTimer = useRef(null);

  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const startPad = firstDay.getDay();
  const totalCells = Math.ceil((startPad + lastDay.getDate()) / 7) * 7;

  const cells = useMemo(() => {
    const list = [];
    for (let i = 0; i < totalCells; i += 1) {
      const dayNum = i - startPad + 1;
      if (dayNum < 1 || dayNum > lastDay.getDate()) {
        list.push(null);
      } else {
        const dateObj = new Date(year, month - 1, dayNum);
        list.push(formatLocalDate(dateObj));
      }
    }
    return list;
  }, [totalCells, startPad, lastDay, year, month]);

  const getDayData = (dateStr) => {
    if (!dateStr || !data) return null;
    if (isTeamView) {
      const allTasks =
        data.users?.flatMap((u) => u.days?.[dateStr]?.tasks || []) || [];
      const types =
        data.users?.map((u) => u.days?.[dateStr]?.type || "WORKING") || [];
      const type = types.includes("HOLIDAY")
        ? "HOLIDAY"
        : types.length > 0 && types.every((t) => t === "WEEKLY_OFF")
          ? "WEEKLY_OFF"
          : "WORKING";
      const holiday = data.holidays?.find((h) => h.date === dateStr);
      return {
        type,
        holidayName: holiday?.name || null,
        taskCount: allTasks.length,
        tasks: allTasks,
      };
    }
    return data?.days?.[dateStr] || null;
  };

  const today = formatLocalDate(new Date());
  const selectedUser = isTeamView ? null : data?.user || null;

  const handleClick = (dateStr) => {
    setSelectedDay(dateStr);
    if (!isAdmin) return;
    if (clickTimer.current) clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(() => {
      setPopoverDay(dateStr);
    }, 250);
  };

  const handleDoubleClick = (dateStr) => {
    if (clickTimer.current) clearTimeout(clickTimer.current);
    setPopoverDay(null);
    onDayDoubleClick(dateStr);
  };

  const handleContextMenu = (e, dateStr, dayInfo) => {
    e.preventDefault();
    setContextMenu({
      x: Math.min(e.clientX, window.innerWidth - 220),
      y: Math.min(e.clientY, window.innerHeight - 300),
      dateStr,
      dayInfo,
      user: selectedUser,
    });
  };

  const handleContextAction = async (action, { dateStr, note }) => {
    const userId = contextMenu?.user?.id;

    switch (action) {
      case "MARK_LEAVE":
        if (userId) {
          await plannerAPI.upsertDayOverride(userId, {
            date: dateStr,
            type: "OFF",
            note: String(note || "").trim() || "Leave",
          });
          await onRefresh();
        }
        break;
      case "MARK_HALF_DAY":
        if (userId) {
          await plannerAPI.upsertDayOverride(userId, {
            date: dateStr,
            type: "HALF_DAY",
          });
          await onRefresh();
        }
        break;
      case "MARK_ONE_OFF_WORKING":
        if (userId) {
          await plannerAPI.upsertDayOverride(userId, {
            date: dateStr,
            type: "WORKING",
            note: "One-off working day",
          });
          await onRefresh();
        }
        break;
      case "RESTORE_DAY":
      case "REMOVE_OVERRIDE":
        if (userId) {
          await plannerAPI.removeDayOverride(userId, dateStr);
          await onRefresh();
        }
        break;
      case "MARK_RECURRING_OFF":
        if (userId && selectedUser) {
          setConfirmModal({
            type: "REMOVE_WORKING_DAY",
            dateStr,
            user: selectedUser,
            currentWorkingDays: data?.workingDays || [],
          });
        }
        break;
      case "VIEW_TASKS":
        onDayDoubleClick(dateStr);
        break;
      default:
        break;
    }

    setContextMenu(null);
  };

  return (
    <div className="month-calendar">
      <h3 className="month-title">
        {MONTH_NAMES[month - 1]} {year}
      </h3>

      <div className="calendar-grid">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="calendar-day-header">
            {d}
          </div>
        ))}

        {cells.map((dateStr, i) => {
          if (!dateStr) {
            return <div key={i} className="calendar-cell empty" />;
          }

          const dayData = getDayData(dateStr);
          const isToday = dateStr === today;
          const isSelected = selectedDay === dateStr;
          const style = getCellStyle(dayData, isSelected);

          return (
            <div
              key={dateStr}
              className={`calendar-cell ${isToday ? "today" : ""} ${
                isSelected ? "selected" : ""
              } ${dayData?.type?.toLowerCase() || ""}`}
              style={style}
              onClick={() => handleClick(dateStr)}
              onDoubleClick={() => handleDoubleClick(dateStr)}
              onContextMenu={(e) => handleContextMenu(e, dateStr, dayData)}
              title="Double-click to see week view"
            >
              <span className={`cell-date ${isToday ? "today-dot" : ""}`}>
                {Number(dateStr.split("-")[2])}
              </span>

                            {dayData?.holidayName && (
                <span className="cell-holiday-name">{dayData.holidayName}</span>
              )}

              {!isTeamView && dayData?.type === "OFF" && (
                <span className="cell-holiday-name">
                  {dayData?.overrideNote || "Leave"}
                </span>
              )}
{dayData?.taskCount > 0 && (
                <div className="cell-task-dots">
                  {[...Array(Math.min(dayData.taskCount, 3))].map((_, idx) => (
                    <span key={idx} className="task-dot" />
                  ))}
                  {dayData.taskCount > 3 && (
                    <span className="task-dot-more">
                      +{dayData.taskCount - 3}
                    </span>
                  )}
                </div>
              )}

              {/* {dayData?.source === "USER_OVERRIDE" && (
                <span className="override-indicator" title="Custom schedule">
                  {dayData.type === "OFF" && "??"}
                  {dayData.type === "HALF_DAY" && "?"}
                  {dayData.type === "WORKING" && "?"}
                </span>
              )} */}
            </div>
          );
        })}
      </div>

      {popoverDay && isAdmin && (
        <DayPopover
          dateStr={popoverDay}
          dayData={getDayData(popoverDay)}
          isAdmin={isAdmin}
          isTeamView={isTeamView}
          users={data?.users}
          onClose={() => setPopoverDay(null)}
          onDoubleClick={() => {
            setPopoverDay(null);
            onDayDoubleClick(popoverDay);
          }}
          onRefresh={onRefresh}
        />
      )}

      {contextMenu && (
        <PlannerContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          dateStr={contextMenu.dateStr}
          dayInfo={contextMenu.dayInfo}
          user={contextMenu.user}
          isAdmin={isAdmin}
          onAction={handleContextAction}
          onClose={() => setContextMenu(null)}
        />
      )}

      {confirmModal && (
        <RecurringScheduleConfirmModal
          user={confirmModal.user}
          dateStr={confirmModal.dateStr}
          action={confirmModal.type}
          currentWorkingDays={confirmModal.currentWorkingDays}
          onConfirm={async ({ newWorkingDays, effectiveFrom }) => {
            await plannerAPI.updateUserSchedule(confirmModal.user.id, {
              workingDays: newWorkingDays,
              workStartTime: data?.workStartTime,
              workEndTime: data?.workEndTime,
              effectiveFrom,
            });
            setConfirmModal(null);
            onRefresh();
          }}
          onClose={() => setConfirmModal(null)}
        />
      )}
    </div>
  );
}



